import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Plus, MagnifyingGlass, UploadSimple } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "../components/ui/tabs";

const STATUSES = ["all", "lead", "prospect", "active", "vip", "supplier", "investor"];
const NEW_HOTEL_CATEGORY = "Nieuw hotel - nog niet benaderd";

const statusTone = {
  lead: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  prospect: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  vip: "bg-primary/15 text-primary border-primary/40",
  supplier: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  investor: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

const EMPTY = {
  company_name: "",
  country: "",
  region: "",
  city: "",
  status: "lead",
  email: "",
  phone: "",
  website: "",
  linkedin: "",
  vat_number: "",
  category: "",
  notes: "",
};

export default function Customers() {
  const nav = useNavigate();
  const showNewHotels = new URLSearchParams(window.location.search).get("newHotels") === "1";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState(showNewHotels ? NEW_HOTEL_CATEGORY : "all");
  const [region, setRegion] = useState("all");
  const [city, setCity] = useState("all");
  const [untouchedOnly, setUntouchedOnly] = useState(showNewHotels);
  const [facets, setFacets] = useState({ categories: [], regions: [], cities: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (category !== "all") params.category = category;
    if (region !== "all") params.region = region;
    if (city !== "all") params.city = city;
    if (untouchedOnly) params.untouched = true;
    if (q) params.q = q;

    const { data } = await api.get("/customers", { params });
    setItems(data);
  };

  const loadFacets = async () => {
    const { data } = await api.get("/customers/facets");
    setFacets(data);
  };

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category, region, city, untouchedOnly]);
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    /* eslint-disable-next-line */
  }, [q]);

  const submit = async () => {
    if (!form.company_name) return;
    setSaving(true);
    try {
      await api.post("/customers", form);
      setOpen(false);
      setForm(EMPTY);
      await load();
      await loadFacets();
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async () => {
    if (!csvFile) return;

    const formData = new FormData();
    formData.append("file", csvFile);
    setImporting(true);

    try {
      const { data } = await api.post("/imports/customers", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await load();
      await loadFacets();
    } catch (err) {
      console.error(err);
      alert("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const toggleNewHotels = () => {
    const next = !untouchedOnly;
    setUntouchedOnly(next);
    setCategory(next ? NEW_HOTEL_CATEGORY : "all");
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline mb-2">- Customer book</div>
          <h1 className="font-display text-4xl tracking-tight leading-none">Customers</h1>
          <p className="text-muted-foreground text-sm mt-2">A 360 view of every relationship in your portfolio.</p>
        </div>

        <div className="flex gap-2">
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <UploadSimple size={14} className="mr-1.5" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Import customers</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(event) => setCsvFile(event.target.files?.[0])}
                />
                <Button onClick={importCsv} disabled={!csvFile || importing}>
                  {importing ? "Uploading..." : "Upload CSV"}
                </Button>
                {importResult && (
                  <div className="rounded-lg border p-4 text-sm">
                    <div>Records found: {importResult.rows_found}</div>
                    <div className="mt-3 font-medium">Columns:</div>
                    <div className="max-h-64 overflow-y-auto rounded border p-3">
                      <ul className="list-disc ml-5 space-y-1">
                        {importResult.columns?.map((column) => (
                          <li key={column}>{column}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary text-primary-foreground" data-testid="new-customer-btn">
                <Plus size={14} className="mr-1.5" />
                New customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">New customer</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2 space-y-1.5">
                  <Label className="overline">Company *</Label>
                  <Input data-testid="cust-company" value={form.company_name} onChange={(event) => setForm({ ...form, company_name: event.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Email</Label>
                  <Input data-testid="cust-email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Phone</Label>
                  <Input data-testid="cust-phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Country</Label>
                  <Input value={form.country} onChange={(event) => setForm({ ...form, country: event.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Province/region</Label>
                  <Input value={form.region} onChange={(event) => setForm({ ...form, region: event.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">City</Label>
                  <Input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Status</Label>
                  <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                    <SelectTrigger data-testid="cust-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.filter((item) => item !== "all").map((item) => (
                        <SelectItem key={item} value={item}>{item}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Category</Label>
                  <Input value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="overline">Website</Label>
                  <Input value={form.website} onChange={(event) => setForm({ ...form, website: event.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="cust-save">{saving ? "Saving..." : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[280px] max-w-xl">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Search company, email, city, province..." className="pl-9" data-testid="customers-search" />
        </div>
        <Button
          variant={untouchedOnly ? "default" : "outline"}
          className="rounded-full"
          onClick={toggleNewHotels}
          data-testid="customers-new-hotels-filter"
        >
          Nieuwe hotels
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            {STATUSES.map((item) => (
              <TabsTrigger key={item} value={item} className="text-xs capitalize" data-testid={`customers-filter-${item}`}>{item}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[240px]" data-testid="customers-category-filter">
            <SelectValue placeholder="Categorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle categorieen</SelectItem>
            {facets.categories?.map((value) => (
              <SelectItem key={value} value={value}>{value}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-[210px]" data-testid="customers-region-filter">
            <SelectValue placeholder="Provincie/regio" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle provincies</SelectItem>
            {facets.regions?.map((value) => (
              <SelectItem key={value} value={value}>{value}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="w-[190px]" data-testid="customers-city-filter">
            <SelectValue placeholder="Stad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle steden</SelectItem>
            {facets.cities?.map((value) => (
              <SelectItem key={value} value={value}>{value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr className="text-left">
              <th className="px-5 py-3 overline">Company</th>
              <th className="px-5 py-3 overline">Status</th>
              <th className="px-5 py-3 overline">Location</th>
              <th className="px-5 py-3 overline">Category</th>
              <th className="px-5 py-3 overline">Next follow-up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((customer) => (
              <tr
                key={customer.id}
                onClick={() => nav(`/customers/${customer.id}`)}
                className="hover:bg-muted/30 cursor-pointer transition-colors"
                data-testid={`customer-row-${customer.id}`}
              >
                <td className="px-5 py-4">
                  <div className="font-medium">{customer.company_name}</div>
                  <div className="text-xs text-muted-foreground">{customer.email || "-"}</div>
                  {customer.untouched_by_agent && (
                    <div className="mt-1 text-xs text-primary">Nieuw hotel - nog niet benaderd door agent</div>
                  )}
                </td>
                <td className="px-5 py-4">
                  <Badge variant="outline" className={`capitalize text-xs ${statusTone[customer.status] || ""}`}>{customer.status}</Badge>
                </td>
                <td className="px-5 py-4 text-muted-foreground">
                  <div>{customer.city || customer.region || customer.country || "-"}</div>
                  <div className="text-xs">{[customer.region, customer.country].filter(Boolean).join(" / ")}</div>
                </td>
                <td className="px-5 py-4 text-muted-foreground">{customer.category || "-"}</td>
                <td className="px-5 py-4 text-muted-foreground">
                  {customer.next_followup ? new Date(customer.next_followup).toLocaleDateString() : "-"}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">No customers match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
